import {
  RiskProfileQuestionDto,
  ClientSelector,
  PostRiskProfileRepsonseDto,
  RiskProfileResultDto,
  RiskProfileRequestDto,
  RiskProfileValidateQuestionDto,
  RiskProfileValidationErrorDto
} from "@/api/ClientPortalApiClient";
import {
  Module,
  VuexModule,
  MutationAction,
  Mutation,
  Action
} from "vuex-module-decorators";
import ApiFactory from "@/api/ApiFactory";
import QuestionResponse from "@/views/riskProfile/QuestionResponse";
import { UserStore, LoadingSpinnerService } from "@/store";
import AlertService from "@/services/alert.service";
import StatusCodes from "http-status-codes";
import moment from "moment";

export interface IRiskProfileState {
  questions: RiskProfileQuestionDto[];
  responses: QuestionResponse[];
  results: RiskProfileResultDto;
  requests: RiskProfileRequestDto[];
  selectedRequest: RiskProfileRequestDto;
  validationMessages: RiskProfileValidationErrorDto[];
}

@Module({ name: "riskProfileStore", namespaced: true })
export class RiskProfileStoreModule extends VuexModule
  implements IRiskProfileState {
  requests: RiskProfileRequestDto[] = [];
  results: RiskProfileResultDto = new RiskProfileResultDto();
  responses: QuestionResponse[] = [];
  questions: RiskProfileQuestionDto[] = [];
  selectedRequest: RiskProfileRequestDto = new RiskProfileRequestDto();
  validationMessages: RiskProfileValidationErrorDto[] = [];

  @Mutation
  // Add the response for a given question, replacing the existing respinse if necessary
  private update_question_response(response: QuestionResponse) {
    const existingItem = this.responses.findIndex(
      x =>
        x.questionId === response.questionId &&
        x.requestId == response.requestId
    );
    if (existingItem > -1) {
      this.responses.splice(existingItem, 1);
    }
    this.responses.push(response);
  }

  @Mutation
  private questions_loaded(questions: RiskProfileQuestionDto[]) {
    this.questions = questions;
  }

  @Mutation
  private clear_responses() {
    this.responses = [];
  }

  @Mutation
  private results_loaded(result: RiskProfileResultDto) {
    this.results = result;
  }

  @Mutation
  private requests_loaded(requests: RiskProfileRequestDto[]) {
    this.requests = requests;
  }

  @Mutation
  private request_completed() {
    this.selectedRequest.completedDate = new Date().toISOString();
    const update = this.requests.find(x => x.id == this.selectedRequest.id);
    if (update) {
      update.completedDate = this.selectedRequest.completedDate;
    }
  }

  @Mutation
  private select_request(id: number) {
    const selected = this.requests.find(x => x.id === id);
    this.selectedRequest = selected || new RiskProfileRequestDto();
  }

  @Mutation
  private reset_to_initial_state() {
    this.requests = [];
    this.results = new RiskProfileResultDto();
    this.responses = [];
    this.questions = [];
    this.selectedRequest = new RiskProfileRequestDto();
    this.validationMessages = [];
  }

  @Mutation
  private set_validation_messages(messages: RiskProfileValidationErrorDto[]) {
    this.validationMessages = messages;
  }

  @Action
  async SetResponse(response: QuestionResponse): Promise<boolean> {
    this.update_question_response(response);

    const api = ApiFactory.CreateDataClient();
    const checkResponses: RiskProfileValidateQuestionDto[] = [];
    this.responses.forEach(r => {
      if (r.requestId === this.selectedRequest.id) {
        const validate = new RiskProfileValidateQuestionDto();
        validate.questionId = r.questionId;
        validate.answers = r.selectedValues;
        checkResponses.push(validate);
      }
    });

    var errors = await api.riskProfile_PostValidateResponses(checkResponses);
    if (errors) {
      this.set_validation_messages(errors);
    }

    return true;
  }

  @Action
  // Load the questions from the API if they haven't already been loaded
  async LoadQuestions(): Promise<boolean> {
    try {
      if (this.questions.length === 0) {
        const api = ApiFactory.CreateDataClient();
        const getQuestions = await api.riskProfile_GetQuestions();
        if (getQuestions) {
          this.questions_loaded(getQuestions);
          return true;
        } else {
          return false;
        }
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  @Action
  // Post the responses to the API
  async CompleteQuestionnaire(): Promise<boolean> {
    const api = ApiFactory.CreateDataClient();

    try {
      // Populate a list with the responses
      const postResponses = new Array<PostRiskProfileRepsonseDto>();
      const relevantResponses = this.responses.filter(
        x => x.requestId == this.selectedRequest.id
      );
      for (let res of relevantResponses) {
        for (let val of res.selectedValues) {
          postResponses.push(new PostRiskProfileRepsonseDto({ answerId: val }));
        }
      }

      // Post to the API
      await api.riskProfile_PostContactResponses(
        UserStore.profileId,
        this.selectedRequest.clientSelector,
        postResponses
      );

      // Get the result from the API
      const result = await api.riskProfile_GetContactResult(
        UserStore.profileId,
        this.selectedRequest.clientSelector
      );

      if (result) {
        this.results_loaded(result);
        return true;
      }

      return true;
    } catch (e) {
      // Handle error properly
      return false;
    }
  }

  @Action
  // Get the result of the risk profile
  async LoadResult(): Promise<boolean> {
    const api = ApiFactory.CreateDataClient();
    try {
      const result = await api.riskProfile_GetContactResult(
        UserStore.profileId,
        this.selectedRequest.clientSelector
      );

      if (result) {
        this.results_loaded(result);
        return true;
      }
    } catch (e) {
      return false;
    }

    return false;
  }

  @Action
  // Get the result of the risk profile
  async LoadRequests(): Promise<boolean> {
    if (this.requests.length > 0) {
      return true;
    }
    const api = ApiFactory.CreateDataClient();
    try {
      const result = await api.riskProfile_GetRequests(UserStore.profileId);

      if (result) {
        this.requests_loaded(result);
        return true;
      }
    } catch (e) {
      return false;
    }

    return false;
  }

  @Action
  // Get the result of the risk profile
  async CompleteRequest(): Promise<boolean> {
    if (this.selectedRequest.id <= 0) {
      return false;
    }
    const api = ApiFactory.CreateDataClient();
    try {
      const result = await api.riskProfile_CompleteRequest(
        UserStore.profileId,
        this.selectedRequest.clientSelector
      );

      if (result.status == StatusCodes.OK) {
        this.request_completed();
        return true;
      }
    } catch (e) {
      return false;
    }

    return false;
  }

  @Action
  SetSelectedRequest(id: number) {
    this.select_request(id);
  }

  @Action
  Reset() {
    this.reset_to_initial_state();
  }

  get Responses(): QuestionResponse[] {
    return this.responses.filter(x => x.requestId === this.selectedRequest.id);
  }

  get AllResponses(): QuestionResponse[] {
    return this.responses;
  }

  get Questions(): RiskProfileQuestionDto[] {
    return this.questions;
  }

  get AllQuestionsAnswered(): boolean {
    let success = true;
    this.questions.forEach(q => {
      const response = this.responses.find(
        s => s.questionId == q.id && s.requestId == this.selectedRequest.id
      );
      if (
        q.answerRequired &&
        (!response || response.selectedValues.length === 0)
      ) {
        success = false;
      }
    });

    return success;
  }

  get Requests(): RiskProfileRequestDto[] {
    return this.requests;
  }

  get Results(): RiskProfileResultDto {
    return this.results;
  }

  get SelectedRequest(): RiskProfileRequestDto {
    return this.selectedRequest;
  }

  get ProfileIsComplete(): boolean {
    return (
      moment(this.selectedRequest.completedDate) >
      moment(this.selectedRequest.sentDate)
    );
  }

  get ValidationMessages(): RiskProfileValidationErrorDto[] {
    return this.validationMessages;
  }
}
